import { AxiosError } from "axios";

/**
 * 對照 lpg-app-api `platform-order-drafts` 模組的錯誤回應，將後端錯誤轉成友善中文訊息。
 *
 * 後端錯誤分兩類：
 * 1. 結構化錯誤：`response.data.code`（機器可讀，優先對應）
 * 2. 純訊息錯誤：`response.data.message`（以關鍵字比對）
 *
 * 對應不到時回傳呼叫端給的 fallback（各操作的通用訊息）。
 */

interface DraftErrorPayload {
  code?: string;
  message?: string | string[];
  [key: string]: unknown;
}

// 有 code 的結構化錯誤 → 友善訊息
const DRAFT_ERROR_CODE_MESSAGES: Record<string, string> = {
  GAS_ITEM_DUPLICATE_LINE: "這筆瓦斯已在訂單中，請直接調整數量，不需重複新增",
  COMMODITY_ITEM_DUPLICATE_LINE: "這項商品已在訂單中，請直接調整數量，不需重複新增",
  GAS_LIST_INTERNAL_DUPLICATE: "瓦斯清單有重複品項，請確認後再送出",
  COMMODITY_LIST_INTERNAL_DUPLICATE: "商品清單有重複品項，請確認後再送出",
  LINE_ITEM_NOT_FOUND: "找不到該品項，可能已被移除，請重新整理後再試",
  INVALID_GAS_PAYLOAD: "商品資料已更新（價格或規格有異動），請重新整理後再試",
};

// 沒有 code、只有 message 的錯誤 → 以關鍵字比對（順序由「特定」到「通用」，find 取第一個命中）
const DRAFT_ERROR_MESSAGE_KEYWORDS: ReadonlyArray<{ match: string; message: string }> = [
  { match: "Cannot edit a draft that is submitting", message: "訂單正在送出中，暫時無法修改" },
  { match: "does not belong to draft customer_address_id", message: "安裝位置與配送地址不符，請重新選擇安裝位置" },
  { match: "moved to another address", message: "安裝位置與配送地址不符，請重新選擇安裝位置" },
  { match: "Address not found or deleted", message: "配送地址不存在或已被刪除，請重新選擇" },
  { match: "replacing_customer_delivery_id and replacing_spec", message: "換桶資料不完整，請重新設定" },
  { match: "Draft not found", message: "找不到訂單草稿，請重新整理後再試" },
  { match: "Draft does not belong to this customer", message: "無權限操作此訂單草稿" },
  // 通用「安裝位置不存在」放最後，避免蓋掉上面更精確的 Address / customer_delivery 訊息
  { match: "not found or deleted", message: "安裝位置不存在或已被刪除，請重新選擇" },
];

/**
 * 跨地址 race 判定：customer_delivery_id 屬於別的地址、或地址在 draft 與 submit 之間被改動時，
 * BE 會回 400。呼叫端應退回「不帶 customer_delivery_id」重試一次，讓 BE 依內容 content-match upsert。
 * - draft-create / item path：「does not belong to draft customer_address_id=」
 * - submit path：「moved to another address between draft and submit」
 */
export const isDraftAddressMismatch = (error: unknown): boolean => {
  const data = (error as AxiosError<DraftErrorPayload>)?.response?.data;
  const rawMessage = Array.isArray(data?.message) ? data?.message.join(" ") : data?.message;
  if (!rawMessage) return false;
  return rawMessage.includes("does not belong to draft customer_address_id") || rawMessage.includes("moved to another address");
};

/**
 * 將後端錯誤轉成可直接顯示給使用者的中文訊息。
 * @param error    catch 到的錯誤（預期為 AxiosError）
 * @param fallback 對應不到任何已知錯誤時的通用訊息（例如「新增商品失敗」）
 */
export const getDraftErrorMessage = (error: unknown, fallback: string): string => {
  const data = (error as AxiosError<DraftErrorPayload>)?.response?.data;

  // 1) 結構化 code 優先
  if (data?.code && DRAFT_ERROR_CODE_MESSAGES[data.code]) {
    return DRAFT_ERROR_CODE_MESSAGES[data.code];
  }

  // 2) message 關鍵字比對
  const rawMessage = Array.isArray(data?.message) ? data?.message.join(" ") : data?.message;
  if (rawMessage) {
    const hit = DRAFT_ERROR_MESSAGE_KEYWORDS.find((k) => rawMessage.includes(k.match));
    if (hit) return hit.message;
  }

  // 3) 通用 fallback
  return fallback;
};
